import React, {useEffect, useState} from 'react';
import {Button, Row, Col, Form, Input, Collapse, message} from 'antd';
import {CommandDto, CommandDtoCommandTypeEnum, LocateDto, PasswordControllerApi, TimeOffset} from "../../api";
import {DeleteOutlined, EditOutlined} from '@ant-design/icons';
import {Link} from "react-router-dom";
import CheckStatusCommandModal from "../modal/commands/CheckStatusCommandModal";
import CompareValueCommandModal from "../modal/commands/CompareValueCommandModal";
import NavigateCommandModal from "../modal/commands/NavigateCommandModal";
import ExecuteCommandModal from "../modal/commands/ExecuteCommandModal";
import PrefabCommandModal from "../modal/commands/PrefabCommandModal";
import SaveValueCommandModal from "../modal/commands/SaveValueCommandModal";
import WaitCommandModal from "../modal/commands/WaitCommandModal";
import DialogCommandModal from "../modal/commands/DialogCommandModal";
import '../../styles.css';
import PauseCommandModal from "../modal/commands/PauseCommandModal";
import ImportTestDataCommandModal from "../modal/commands/ImportTestDataCommandModal";
import {formatDateTime, getTimeOffsetString} from "../../utils/Utils";
import {COLORS} from "../../utils/Colors";

interface CommandsOutputCardsProps {
    commandDto: CommandDto;
    commandIndex: number;
    onRemoveCommand: (commandDto: CommandDto) => void;
    onUpdateCommand: (commandDto: CommandDto, commandIndex: number) => void;
}

const labelColSpan = 6;
const wrapperColSpan = 118;


const CommandsOutputCards: React.FC<CommandsOutputCardsProps> = ({
                                                                     commandDto,
                                                                     commandIndex,
                                                                     onRemoveCommand,
                                                                     onUpdateCommand
                                                                 }) => {


    const [commandStates, setCommandStates] = useState({
        isNavigateCommand: false,
        isExecuteCommand: false,
        isCheckCommand: false,
        isSaveValueCommand: false,
        isCompareValueCommand: false,
        isPrefabCommand: false,
        isRequestCommand: false,
        isWaitCommand: false,
        isDialogCommand: false,
        isPauseCommand: false,
        isImportTestDataCommand: false,
        isPauseRecordCommand: false
    });

    const [index, setIndex] = useState<number>(0);
    const passwordControllerApi = new PasswordControllerApi();

    useEffect(() => {
        setCommandStates({
            isNavigateCommand: commandDto.commandType === "NAVIGATE",
            isExecuteCommand: commandDto.commandType === "EXECUTE",
            isCheckCommand: commandDto.commandType === "CHECK_STATUS",
            isSaveValueCommand: commandDto.commandType === "SAVE_VALUE",
            isCompareValueCommand: commandDto.commandType === "COMPARE_VALUE",
            isPrefabCommand: commandDto.commandType === "PREFAB",
            isRequestCommand: commandDto.commandType === "REQUEST",
            isWaitCommand: commandDto.commandType === "WAIT",
            isDialogCommand: commandDto.commandType === "DIALOG",
            isPauseCommand: commandDto.commandType === "PAUSE",
            isImportTestDataCommand: commandDto.commandType === "IMPORT",
            isPauseRecordCommand: commandDto.commandType === "PAUSE_RECORD"
        });
    }, [commandDto.commandType]);

    useEffect(() => {
        setIndex(commandIndex);
    }, [commandIndex]);


    const handleRemoveClick = () => {
        // onRemoveCommand-Funktion aufrufen, um das commandDto zu entfernen
        onRemoveCommand(commandDto);
    };

    const getFormItemRowsForExecuteCommand = (commandDto: CommandDto) => {
        if (commandDto === undefined) return null;
        const shouldDisplayTargetDto = commandDto.executeTarget !== null && commandDto.executeTarget !== undefined;
        const shouldExecuteValueDto = commandDto.executeValue !== null && commandDto.executeValue !== undefined;

        let locateDto = commandDto.locateDto ? getDeepestLocateDto(commandDto.locateDto): null;

        return (
            <>
                <Row gutter={16}>
                    {getFormItemRowForLocateDto(locateDto, false)}
                    <Col span={12}>
                        <Form.Item label="Execute Option" className="custom-form-item" labelCol={{span: labelColSpan}}
                                   wrapperCol={{span: wrapperColSpan}}>
                            <Input value={commandDto.executeOption} readOnly disabled/>
                        </Form.Item>
                    </Col>
                    {shouldExecuteValueDto && (
                        <Col span={12}>
                            <Form.Item label="Execute Value" className="custom-form-item" labelCol={{span: labelColSpan}}
                                       wrapperCol={{span: wrapperColSpan}}>
                                <Input
                                    value={commandDto.passwordDto ? "Passwort: " + commandDto.passwordDto.name : commandDto.executeValue}
                                    readOnly disabled/>
                            </Form.Item>
                        </Col>
                    )}
                </Row>
                {shouldDisplayTargetDto && getFormItemRowForLocateDto(commandDto.executeTarget, true)}
            </>
        );
    }

    const getFormItemRowForLocateDto = (locateDto, isTarget) => {
        if (!locateDto) return null;

        const fields = [
            {label: 'Locate-Option', value: locateDto.locateOption},
            {label: 'Aria-Role', value: locateDto.role},
            {label: 'Eindeutig', value: locateDto.exact ? "Ja" : "Nein"},
            {label: 'Locate-Wert', value: locateDto.checkValue, condition: !isTarget},
            {label: 'Locate-Pattern', value: locateDto.patternValue?.toString() ?? null},
            {label: 'Index', value: locateDto.nthElement}
        ];

        return (
            fields.map((field, index) => (
                (field.value !== undefined && field.value !== null && (field.condition === undefined || field.condition)) && (
                    <Col span={12} key={index}>
                        <Form.Item
                            label={field.label}
                            className="custom-form-item"
                            labelCol={{span: labelColSpan}}
                            wrapperCol={{span: wrapperColSpan}}
                        >
                            <Input value={field.value} readOnly disabled/>
                        </Form.Item>
                    </Col>
                )
            ))
        );
    };

    const getFormItemRowsForCheckStatusCommand = (commandDto: CommandDto) => {
        if (commandDto === undefined) return null;

        let locateDto = commandDto.locateDto ? getDeepestLocateDto(commandDto.locateDto): null;

        return (
            <>
                <Row gutter={18}>
                    {getFormItemRowForLocateDto(locateDto, false)}
                    <Col span={12}>
                        <Form.Item label="Prüfoption" className="custom-form-item" labelCol={{span: labelColSpan}}
                                   wrapperCol={{span: wrapperColSpan}}>
                            <Input value={commandDto.checkStatusOption} readOnly disabled/>
                        </Form.Item>
                    </Col>
                    {commandDto.checkStatusValue &&
                        <Col span={12}>
                          <Form.Item label="Prüfwert" className="custom-form-item" labelCol={{span: labelColSpan}}
                                     wrapperCol={{span: wrapperColSpan}}>
                            <Input value={commandDto.checkStatusValue} readOnly disabled/>
                          </Form.Item>
                        </Col>}
                </Row>
            </>
        );
    }

    const getFormItemRowsSaveValueCommand = (commandDto: CommandDto) => {
        if (commandDto === undefined) return null;

        let locateDto = commandDto.locateDto ? getDeepestLocateDto(commandDto.locateDto): null;

        return (
            <>
                <Row gutter={18}>
                    {getFormItemRowForLocateDto(locateDto, false)}
                    <Col span={12}>
                        <Form.Item label="Option" className="custom-form-item" labelCol={{span: labelColSpan}}
                                   wrapperCol={{span: wrapperColSpan}}>
                            <Input value={commandDto.valueOption} readOnly disabled/>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Variable" className="custom-form-item" labelCol={{span: labelColSpan}}
                                   wrapperCol={{span: wrapperColSpan}}>
                            <Input value={commandDto.variableName} readOnly disabled/>
                        </Form.Item>
                    </Col>
                    {commandDto.jsonPath && <Col span={12}>
                      <Form.Item label="Json-Pfad" className="custom-form-item" labelCol={{span: labelColSpan}}
                                 wrapperCol={{span: wrapperColSpan}}>
                        <Input value={commandDto.jsonPath} readOnly disabled/>
                      </Form.Item>
                    </Col>}
                    {commandDto.attribute && (
                        <Col span={12}>
                            <Form.Item label="Attribut" className="custom-form-item" labelCol={{span: labelColSpan}}
                                       wrapperCol={{span: wrapperColSpan}}>
                                <Input value={commandDto.attribute} readOnly disabled/>
                            </Form.Item>
                        </Col>
                    )}
                </Row>
            </>
        );
    }

    const getFormItemRowsForCompareValue = (commandDto: CommandDto) => {
        if (commandDto === undefined) return null;

        let locateDto = commandDto.locateDto ? getDeepestLocateDto(commandDto.locateDto): null;

        return (
            <>
                <Row gutter={18}>
                    {getFormItemRowForLocateDto(locateDto, false)}
                    <Col span={12}>
                        <Form.Item label="Option" className="custom-form-item" labelCol={{span: labelColSpan}}
                                   wrapperCol={{span: wrapperColSpan}}>
                            <Input value={commandDto.valueOption} readOnly disabled/>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Vergleich" className="custom-form-item" labelCol={{span: labelColSpan}}
                                   wrapperCol={{span: wrapperColSpan}}>
                            <Input value={commandDto.compareValue} readOnly disabled/>
                        </Form.Item>
                    </Col>
                </Row>
            </>
        );
    }

    const getPrefabForm = (commandDto: CommandDto) => {
        if (commandDto === undefined) return null;

        return (
            <>
                <Row gutter={18}>
                    <Col span={12}>
                        <Form.Item label="ID" className="custom-form-item" labelCol={{span: labelColSpan}}
                                   wrapperCol={{span: wrapperColSpan}}>
                            <Input value={commandDto.prefabId} readOnly disabled/>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Komponente" className="custom-form-item" labelCol={{span: labelColSpan}}
                                   wrapperCol={{span: wrapperColSpan}}>
                            <Input value={commandDto.prefabName} readOnly disabled/>
                        </Form.Item>
                    </Col>
                </Row>
            </>
        );
    }

    const getNavigateForm = (commandDto: CommandDto) => {
        if (commandDto === undefined) return null;

        return (
            <>
                <Row gutter={18}>
                    <Col span={12}>
                        <Form.Item label="URL" className="custom-form-item" labelCol={{span: labelColSpan}}
                                   wrapperCol={{span: wrapperColSpan}}>
                            <Input value={commandDto.navigateUrl} readOnly disabled/>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="System-Tab" className="custom-form-item" labelCol={{span: labelColSpan}}
                                   wrapperCol={{span: wrapperColSpan}}>
                            <Input value={commandDto.systemTab} readOnly disabled/>
                        </Form.Item>
                    </Col>
                </Row>
            </>
        );
    }

    const getWaitForm = (commandDto: CommandDto) => {
        if (commandDto === undefined) return null;

        return (
            <>
                <Row gutter={18}>
                    <Col span={12}>
                        <Form.Item label="Warten (in ms)" className="custom-form-item" labelCol={{span: labelColSpan}}
                                   wrapperCol={{span: wrapperColSpan}}>
                            <Input value={commandDto.waitInMs} readOnly disabled/>
                        </Form.Item>
                    </Col>
                </Row>
            </>
        );
    }

    const getDialogForm = (commandDto: CommandDto) => {
        if (commandDto === undefined) return null;

        return (
            <>
                <Row gutter={18}>
                    <Col span={12}>
                        <Form.Item label="Dialog" className="custom-form-item" labelCol={{span: labelColSpan}}
                                   wrapperCol={{span: wrapperColSpan}}>
                            <Input value={commandDto.dialogAccept ? "Accept" : "Dismiss"} readOnly disabled/>
                        </Form.Item>
                    </Col>
                </Row>
            </>
        );
    }

    const getPauseForm = (commandDto: CommandDto) => {
        if (commandDto === undefined) return null;

        return (
            <>
                <Row gutter={18}>
                    <Col span={12}>
                        <Form.Item label="Test pausieren" className="custom-form-item" labelCol={{span: labelColSpan}}
                                   wrapperCol={{span: wrapperColSpan}}>
                            <Input value={commandDto.timeOffset ? getTimeOffsetString(commandDto.timeOffset) : "Keine Angaben"}
                                   readOnly disabled/>
                        </Form.Item>
                    </Col>
                </Row>
            </>
        );
    }

    const getPauseRecordForm = (commandDto: CommandDto) => {
        if (commandDto === undefined) return null;

        return (
            <>
                <Row gutter={18}>
                    <Col span={12}>
                        <Form.Item label="Aufzeichnen" className="custom-form-item" labelCol={{span: labelColSpan}}
                                   wrapperCol={{span: wrapperColSpan}}>
                            <Input value={"Ja"}
                                   readOnly disabled/>
                        </Form.Item>
                    </Col>
                </Row>
            </>
        );
    }


    // Eine Funktion, um den Typ { [key: string]: string; } in einen String umzuwandeln
    function formatData(data) {
        return Object.entries(data)
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ');
    }

    const getRequestForm = (commandDto: CommandDto) => {
        if (commandDto === undefined) return null;

        return (
            <>
                <Row gutter={18}>
                    <Col span={12}>
                        <Form.Item label="ID" className="custom-form-item" labelCol={{span: labelColSpan}}
                                   wrapperCol={{span: wrapperColSpan}}>
                            <Input value={commandDto.requestId} readOnly disabled/>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Name" className="custom-form-item" labelCol={{span: labelColSpan}}
                                   wrapperCol={{span: wrapperColSpan}}>
                            <Input value={commandDto.requestName} readOnly disabled/>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="URL" className="custom-form-item" labelCol={{span: labelColSpan}}
                                   wrapperCol={{span: wrapperColSpan}}>
                            <Input value={commandDto.requestUrl} readOnly disabled/>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Pfad-Variablen" className="custom-form-item" labelCol={{span: labelColSpan}}
                                   wrapperCol={{span: wrapperColSpan}}>
                            <Input value={commandDto.pathVariables ? formatData(commandDto.pathVariables) : ""} readOnly
                                   disabled/>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label="Antwort-Variables" className="custom-form-item" labelCol={{span: labelColSpan}}
                                   wrapperCol={{span: wrapperColSpan}}>
                            <Input value={commandDto.responseVariables ? formatData(commandDto.responseVariables) : ""} readOnly
                                   disabled/>
                        </Form.Item>
                    </Col>
                </Row>
            </>
        );
    }

    const getImportTestDataForm = (commandDto: CommandDto) => {
        if (commandDto === undefined || commandDto.importTestDataCommandDto === undefined || commandDto.importTestDataCommandDto === null) return null;

        let importTestData = commandDto.importTestDataCommandDto;
        return (
            <>
                <Row gutter={18}>
                    <Col span={12}>
                        <Form.Item label="Test-ID" className="custom-form-item" labelCol={{span: labelColSpan}}
                                   wrapperCol={{span: wrapperColSpan}}>
                            <Input value={importTestData.testId} readOnly disabled/>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Variablen" className="custom-form-item" labelCol={{span: labelColSpan}}
                                   wrapperCol={{span: wrapperColSpan}}>
                            <Input value={importTestData.containsVariables?.join(', ')} readOnly disabled/>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Fehlges. Tests" className="custom-form-item" labelCol={{span: labelColSpan}}
                                   wrapperCol={{span: wrapperColSpan}}>
                            <Input value={importTestData.useFailedTestExecution ? "Ja" : "Nein"} readOnly disabled/>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Verwendete Tests" className="custom-form-item" labelCol={{span: labelColSpan}}
                                   wrapperCol={{span: wrapperColSpan}}>
                            <Input value={importTestData.useUsedTestExecution ? "Ja" : "Nein"} readOnly disabled/>
                        </Form.Item>
                    </Col>
                    {importTestData.timeOffset &&
                        <Col span={12}>
                          <Form.Item label="Zeit-Offset" className="custom-form-item" labelCol={{span: labelColSpan}}
                                     wrapperCol={{span: wrapperColSpan}}>
                            <Input value={getTimeOffsetString(importTestData.timeOffset)} readOnly disabled/>
                          </Form.Item>
                        </Col>}
                    {importTestData.startTs &&
                        <Col span={12}>
                          <Form.Item label="Startzeit" className="custom-form-item" labelCol={{span: labelColSpan}}
                                     wrapperCol={{span: wrapperColSpan}}>
                            <Input value={formatDateTime(importTestData.startTs)} readOnly disabled/>
                          </Form.Item>
                        </Col>
                    }
                </Row>
            </>
        );
    }

    const handleCommandCardUpdate = (commandDto: CommandDto) => {
        onUpdateCommand(commandDto, index);
    }

    const getImportantCommandTypeData = (commandDto: CommandDto) => {
        let infoText = "";
        if (commandDto.description) {
            infoText = commandDto.description + ' (';
        } else {
            infoText = '(';
        }

        switch (commandDto.commandType) {
            case "NAVIGATE":
                return infoText += commandDto.navigateUrl + ')';
            case "EXECUTE":
                return infoText += (commandDto.locateDto ? getDeepestLocateDto(commandDto.locateDto).checkValue : "Undefiniert") + ") " + commandDto.executeOption;
            case "COMPARE_VALUE":
            case "CHECK_STATUS":
                return infoText += (commandDto.locateDto ? getDeepestLocateDto(commandDto.locateDto).checkValue : "Undefiniert") + ")";
            case "SAVE_VALUE":
                return infoText += commandDto.variableName + ')';
            case "PREFAB":
                return infoText += commandDto.prefabName + ')';
            case "REQUEST":
                return infoText += commandDto.requestName + ')';
            case "WAIT":
                return infoText += commandDto.waitInMs + 'ms)';
            case "DIALOG":
                return infoText += (commandDto.dialogAccept ? "Accept" : "Dismiss") + ')';
            case "PAUSE":
                return infoText += commandDto.timeOffset ? getTimeOffsetString(commandDto.timeOffset) + ")" : ")"
            case "IMPORT":
                return infoText += commandDto.importTestDataCommandDto ? "Daten von Test-ID " + commandDto.importTestDataCommandDto.testId + ")" : ")"
            case "PAUSE_RECORD":
                return infoText += 'für Aufzeichnung)'
            default:
                return infoText += '?)';
        }
    }

    const resolveCommandName = (commandType: CommandDtoCommandTypeEnum) => {
        switch (commandType) {
            case "NAVIGATE":
                return "Navigation"
            case "EXECUTE":
                return "Ausführen"
            case "COMPARE_VALUE":
                return "Vergleichen"
            case "CHECK_STATUS":
                return "Zustandsprüfung"
            case "SAVE_VALUE":
                return "Speichern"
            case "PREFAB":
                return "Komponente"
            case "REQUEST":
                return "API-Request"
            case "WAIT":
                return "Warten"
            case "DIALOG":
                return "Dialog"
            case "PAUSE":
                return "Pausieren"
            case "IMPORT":
                return "Importieren"
            case "PAUSE_RECORD":
                return "Pausieren"
            default:
                return "Unbekannter Befehl"
        }
    }

    const getDeepestLocateDto = (locateDto: LocateDto): LocateDto => {
        if (locateDto.locate) {
            return getDeepestLocateDto(locateDto.locate);
        } else {
            return locateDto;
        }
    }


    return (
        <Collapse accordion>
            <Collapse.Panel header={
                <div>
                    <span>
                        <strong>{(commandIndex + 1) + " "  + (commandDto.commandType ? resolveCommandName(commandDto.commandType) : "Unbekannter Befehl")} </strong>
                         - {getImportantCommandTypeData(commandDto)}
                    </span>
                </div>}
                            key="1">
                <div>
                    <Button
                        type="primary"
                        danger
                        style={{float: 'right'}}
                        onClick={handleRemoveClick}
                    >
                        <DeleteOutlined/>
                    </Button>
                    {commandDto.commandType === "PREFAB" &&
                        <Link to={process.env.PUBLIC_URL + `/edit/prefab/${commandDto.prefabId}`}>
                          <Button type="default" style={{float: 'right'}}>
                            <EditOutlined/>
                          </Button>
                        </Link>}
                    {commandDto.commandType === "PREFAB" &&
                        <PrefabCommandModal onCommandCardData={handleCommandCardUpdate} initialData={commandDto}/>}
                    {commandDto.commandType === "CHECK_STATUS" &&
                        <CheckStatusCommandModal onCommandCardData={handleCommandCardUpdate} initialData={commandDto}/>}
                    {commandDto.commandType === "COMPARE_VALUE" &&
                        <CompareValueCommandModal onCommandCardData={handleCommandCardUpdate} initialData={commandDto}/>}
                    {commandDto.commandType === "NAVIGATE" &&
                        <NavigateCommandModal onCommandCardData={handleCommandCardUpdate} initialData={commandDto}/>}
                    {commandDto.commandType === "EXECUTE" &&
                        <ExecuteCommandModal onCommandCardData={handleCommandCardUpdate} initialData={commandDto}/>}
                    {commandDto.commandType === "SAVE_VALUE" &&
                        <SaveValueCommandModal onCommandCardData={handleCommandCardUpdate} initialData={commandDto}/>}
                    {commandDto.commandType === "WAIT" &&
                        <WaitCommandModal onCommandCardData={handleCommandCardUpdate} initialData={commandDto}/>}
                    {commandDto.commandType === "DIALOG" &&
                        <DialogCommandModal onCommandCardData={handleCommandCardUpdate} initialData={commandDto}/>}
                    {commandDto.commandType === "PAUSE" &&
                        <PauseCommandModal onCommandCardData={handleCommandCardUpdate} initialData={commandDto}/>}
                    {commandDto.commandType === "IMPORT" &&
                        <ImportTestDataCommandModal onCommandCardData={handleCommandCardUpdate} initialData={commandDto}/>}
                </div>

                <Form
                    layout={'horizontal'}>
                    {commandStates.isNavigateCommand && getNavigateForm(commandDto)}
                    {commandStates.isExecuteCommand && getFormItemRowsForExecuteCommand(commandDto)}
                    {commandStates.isCheckCommand && getFormItemRowsForCheckStatusCommand(commandDto)}
                    {commandStates.isSaveValueCommand && getFormItemRowsSaveValueCommand(commandDto)}
                    {commandStates.isCompareValueCommand && getFormItemRowsForCompareValue(commandDto)}
                    {commandStates.isPrefabCommand && getPrefabForm(commandDto)}
                    {commandStates.isRequestCommand && getRequestForm(commandDto)}
                    {commandStates.isWaitCommand && getWaitForm(commandDto)}
                    {commandStates.isDialogCommand && getDialogForm(commandDto)}
                    {commandStates.isPauseCommand && getPauseForm(commandDto)}
                    {commandStates.isImportTestDataCommand && getImportTestDataForm(commandDto)}
                    {commandStates.isPauseRecordCommand && getPauseRecordForm(commandDto)}
                </Form>
            </Collapse.Panel>
        </Collapse>
    );

};

export default CommandsOutputCards;